import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSpreadsheet, RefreshCw, Trash2, ArrowRight, Loader2, Image as ImageIcon, Upload, Link as LinkIcon } from "lucide-react";

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

/** Per-archive "Photo Drive folder" (gallery whose sub-folders carry "#PO"): set · re-index · status. */
function PhotoFolderRow({ archive }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(archive.photo_folder_url || "");
  const refresh = () => { qc.invalidateQueries({ queryKey: ["report-archives"] }); qc.invalidateQueries({ queryKey: ["report-query"] }); };
  const save = useMutation({
    mutationFn: async () => (await api.patch(`/report/archive/${archive.id}`, { photo_folder_url: url })).data,
    onSuccess: (d) => { toast.success(d.photo_folder_id ? "Photo folder saved — indexing PO folders…" : "Photo folder cleared"); refresh(); },
    onError: (e) => toast.error(errMsg(e, "Could not save the folder")),
  });
  const reindex = useMutation({
    mutationFn: async () => (await api.post(`/report/archive/${archive.id}/index-photos`)).data,
    onSuccess: () => { toast.success("Re-indexing the gallery…"); refresh(); },
    onError: (e) => toast.error(errMsg(e, "Could not start indexing")),
  });
  const indexing = archive.gallery_indexing;
  return (
    <div className="flex flex-wrap items-center gap-2 pl-6 text-[11px]" data-testid={`rpt-archive-photos-${archive.id}`}>
      <ImageIcon size={12} className="text-emerald-700" />
      <span className="text-slate-500">Photos from Drive (folders named “… #PO”)</span>
      <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/drive/folders/…" className="h-7 w-[340px] text-[11px]" data-testid={`rpt-archive-photo-url-${archive.id}`} />
      <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={save.isPending || url === (archive.photo_folder_url || "")} onClick={() => save.mutate()} data-testid={`rpt-archive-photo-save-${archive.id}`}>Save</Button>
      {archive.photo_folder_id && (
        <>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={indexing || reindex.isPending} onClick={() => reindex.mutate()} data-testid={`rpt-archive-photo-reindex-${archive.id}`}>
            {indexing ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />} {indexing ? "Indexing…" : "Re-index"}
          </Button>
          <span className="text-slate-500" data-testid={`rpt-archive-photo-status-${archive.id}`}>
            {indexing ? "reading the gallery…" : archive.gallery_error ? <span className="text-red-700">{archive.gallery_error}</span>
              : archive.gallery_indexed_at ? <><b className="text-slate-700 tabular-nums">{archive.gallery_folders ?? 0}</b> PO folders · indexed {String(archive.gallery_indexed_at).slice(0, 16).replace("T", " ")}</> : "not indexed yet"}
          </span>
        </>
      )}
    </div>
  );
}

/** Existing old worksheets: name · tab · rows · imported · Re-import / Delete. */
function ArchiveList({ archives, onEdit }) {
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ["report-archives"] }); qc.invalidateQueries({ queryKey: ["report-options"] }); qc.invalidateQueries({ queryKey: ["report-query"] }); };
  const reimport = useMutation({
    mutationFn: async (id) => (await api.post(`/report/archive/${id}/reimport`)).data,
    onSuccess: (a) => { toast.success(`Re-imported ${a.row_count} rows from “${a.name}”`); refresh(); },
    onError: (e) => toast.error(errMsg(e, "Re-import failed")),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/report/archive/${id}`)).data,
    onSuccess: () => { toast.success("Old worksheet removed"); refresh(); },
    onError: (e) => toast.error(errMsg(e, "Delete failed")),
  });
  if (!archives.length) return <div className="text-xs text-slate-500" data-testid="rpt-archive-empty">No old worksheet yet — paste a Google Sheet link below.</div>;
  return (
    <div className="space-y-1.5" data-testid="rpt-archive-list">
      {archives.map((a) => (
        <div key={a.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 space-y-1.5" data-testid={`rpt-archive-${a.id}`}>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <FileSpreadsheet size={14} className="text-emerald-700" />
          <span className="font-semibold">{a.name}</span>
          <span className="text-slate-500">{a.source_type === "file" ? <>Excel file “{a.file_name}”{a.sheet_title ? ` · tab “${a.sheet_title}”` : ""}</> : <>tab “{a.sheet_title}”</>} · <b className="text-slate-700 tabular-nums" data-testid={`rpt-archive-rows-${a.id}`}>{a.row_count}</b> rows · imported {String(a.imported_at || "").slice(0, 10)}</span>
          {a.url && <a href={a.url} target="_blank" rel="noreferrer" className="text-blue-700 underline">open sheet</a>}
          <span className="ml-auto flex items-center gap-1">
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onEdit(a)} data-testid={`rpt-archive-edit-${a.id}`}>Mapping</Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={reimport.isPending} onClick={() => reimport.mutate(a.id)} data-testid={`rpt-archive-reimport-${a.id}`}>
              {reimport.isPending && reimport.variables === a.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />} Re-import
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-700 border-red-200 hover:bg-red-50" onClick={() => { if (window.confirm(`Remove “${a.name}” and its ${a.row_count} rows from the report?`)) del.mutate(a.id); }} data-testid={`rpt-archive-del-${a.id}`}>
              <Trash2 size={12} />
            </Button>
          </span>
        </div>
        <PhotoFolderRow archive={a} />
        </div>
      ))}
    </div>
  );
}

/** Header → field mapping table with sample values. */
function MappingTable({ inspect, mapping, setMapping, fields }) {
  const usedBy = (field) => Object.entries(mapping).find(([, f]) => f === field)?.[0];
  return (
    <div className="rounded-md border border-slate-200 overflow-auto max-h-[42vh]" data-testid="rpt-archive-mapping">
      <table className="w-full text-xs">
        <thead className="bg-slate-100 sticky top-0">
          <tr><th className="px-2 py-1.5 text-left font-semibold">Sheet column</th><th className="px-2 py-1.5 text-left font-semibold">Sample values</th><th className="px-2 py-1.5 text-left font-semibold w-52">→ Report field</th></tr>
        </thead>
        <tbody>
          {inspect.headers.map((h, i) => {
            const val = mapping[String(i)] || "";
            return (
              <tr key={i} className={`border-t border-slate-100 ${val ? "bg-emerald-50/40" : ""}`} data-testid={`rpt-archive-col-${i}`}>
                <td className="px-2 py-1 font-medium whitespace-nowrap">{h || <span className="text-slate-400">(column {i + 1})</span>}</td>
                <td className="px-2 py-1 text-slate-500 truncate max-w-[260px]">{inspect.sample.map((r) => r[i]).filter(Boolean).slice(0, 3).join(" · ")}</td>
                <td className="px-2 py-1">
                  <select
                    value={val}
                    onChange={(e) => setMapping((m) => { const n = { ...m }; if (e.target.value) n[String(i)] = e.target.value; else delete n[String(i)]; return n; })}
                    className={`h-7 w-full rounded border bg-white px-1 text-xs ${val ? "border-emerald-500" : "border-slate-300"}`}
                    data-testid={`rpt-archive-map-${i}`}
                  >
                    <option value="">— skip —</option>
                    {fields.map((f) => {
                      const u = usedBy(f.key);
                      return <option key={f.key} value={f.key} disabled={!!u && u !== String(i)}>{f.label}{u && u !== String(i) ? ` (col ${Number(u) + 1})` : ""}</option>;
                    })}
                  </select>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Add a worksheet: Google Sheet link OR Excel/CSV upload → inspect → fix mapping → import (or re-map an existing archive). */
function ArchiveImporter({ edit, onDone }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState(edit?.source_type === "file" ? "file" : "sheet");
  const [url, setUrl] = useState(edit?.url || "");
  const [name, setName] = useState(edit?.name || "");
  const [inspect, setInspect] = useState(null);
  const [mapping, setMapping] = useState({});
  const [uploadId, setUploadId] = useState(edit?.source_type === "file" ? edit.file_id : null);
  const [fileName, setFileName] = useState(edit?.source_type === "file" ? edit.file_name : "");
  const applyInspect = (d) => {
    setInspect(d);
    if (d.upload_id) { setUploadId(d.upload_id); setFileName(d.file_name); }
    setMapping(edit?.mapping && edit.sheet_title === d.sheet_title && edit.header_row_index === d.header_row_index ? edit.mapping : d.suggested_mapping || {});
    if (!name) setName(d.existing?.name || d.spreadsheet_title || "");
  };
  const inspectMut = useMutation({
    mutationFn: async ({ sheet_title, header_row_index, file } = {}) => {
      if (mode === "sheet") return (await api.post("/report/archive/inspect", { url, sheet_title, header_row_index })).data;
      const fd = new FormData();
      if (file) fd.append("file", file); else fd.append("upload_id", uploadId);
      if (sheet_title) fd.append("sheet_title", sheet_title);
      if (header_row_index !== null && header_row_index !== undefined) fd.append("header_row_index", String(header_row_index));
      if (edit?.id) fd.append("archive_id", edit.id);
      return (await api.post("/report/archive/inspect-file", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: applyInspect,
    onError: (e) => toast.error(errMsg(e, mode === "sheet" ? "Could not read the sheet" : "Could not read the file")),
  });
  const importMut = useMutation({
    mutationFn: async () => {
      const common = { sheet_title: inspect.sheet_title, mapping, name, header_row_index: inspect.header_row_index, archive_id: edit?.id || inspect.existing?.id || null };
      if (mode === "sheet") return (await api.post("/report/archive/import", { url, ...common })).data;
      return (await api.post("/report/archive/import-file", { upload_id: uploadId, ...common })).data;
    },
    onSuccess: (a) => {
      toast.success(`Imported ${a.row_count} rows from “${a.name}” — they now show in the report as OLD SHEET`);
      qc.invalidateQueries({ queryKey: ["report-archives"] }); qc.invalidateQueries({ queryKey: ["report-options"] }); qc.invalidateQueries({ queryKey: ["report-query"] });
      onDone();
    },
    onError: (e) => toast.error(errMsg(e, "Import failed")),
  });
  const mappedCount = Object.keys(mapping).length;
  const tab = (m, label, Icon) => (
    <button type="button" onClick={() => { setMode(m); setInspect(null); }} disabled={!!edit && mode !== m}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs font-medium transition-colors ${mode === m ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-500 disabled:opacity-40"}`}
      data-testid={`rpt-archive-mode-${m}`} aria-pressed={mode === m}>
      <Icon size={13} /> {label}
    </button>
  );
  return (
    <div className="space-y-3" data-testid="rpt-archive-importer">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-slate-500 mr-1">Source</span>
        {tab("sheet", "Google Sheet link", LinkIcon)}
        {tab("file", "Excel / CSV file", Upload)}
      </div>
      {mode === "sheet" ? (
        <>
          <div className="flex items-center gap-2">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" className="h-9 text-sm" data-testid="rpt-archive-url" disabled={!!edit} />
            <Button size="sm" className="h-9 bg-slate-900 hover:bg-black text-white shrink-0" disabled={!url.trim() || inspectMut.isPending} onClick={() => inspectMut.mutate({ sheet_title: edit?.sheet_title || null, header_row_index: edit?.header_row_index ?? null })} data-testid="rpt-archive-inspect">
              {inspectMut.isPending ? <Loader2 size={14} className="animate-spin mr-1" /> : <ArrowRight size={14} className="mr-1" />} Read sheet
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">The sheet is read through the Google account connected in Settings — share it with that account if it isn't yours. Only the columns you map are copied.</p>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-dashed border-slate-400 bg-white text-sm text-slate-700 cursor-pointer hover:border-slate-900" data-testid="rpt-archive-file-label">
              <Upload size={14} /> {fileName ? `Replace file (${fileName})` : "Choose Excel (.xlsx) or CSV file…"}
              <input type="file" accept=".xlsx,.xlsm,.csv,.txt" className="hidden" data-testid="rpt-archive-file" onChange={(e) => { const f = e.target.files?.[0]; if (f) inspectMut.mutate({ file: f, sheet_title: edit?.sheet_title || null, header_row_index: edit?.header_row_index ?? null }); e.target.value = ""; }} />
            </label>
            {edit && uploadId && !inspect && (
              <Button size="sm" variant="outline" className="h-9" disabled={inspectMut.isPending} onClick={() => inspectMut.mutate({ sheet_title: edit.sheet_title, header_row_index: edit.header_row_index })} data-testid="rpt-archive-reread-file">
                {inspectMut.isPending ? <Loader2 size={14} className="animate-spin mr-1" /> : <ArrowRight size={14} className="mr-1" />} Read stored file
              </Button>
            )}
            {inspectMut.isPending && <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> reading…</span>}
          </div>
          <p className="text-[11px] text-slate-500">The file is copied into the system (max 15 MB) so Re-import works later. Save .xls as .xlsx first. Only the columns you map are used.</p>
        </>
      )}
      {inspect && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="font-semibold">{inspect.spreadsheet_title}</span>
            {inspect.sheets.length > 1 && (
              <label className="inline-flex items-center gap-1">Tab
                <select value={inspect.sheet_title} onChange={(e) => inspectMut.mutate({ sheet_title: e.target.value })} className="h-7 rounded border border-slate-300 bg-white px-1 text-xs" data-testid="rpt-archive-tab">
                  {inspect.sheets.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            )}
            <label className="inline-flex items-center gap-1" title="Which sheet row holds the column names">Header row
              <select value={inspect.header_row_index} onChange={(e) => inspectMut.mutate({ sheet_title: inspect.sheet_title, header_row_index: Number(e.target.value) })} className="h-7 max-w-[340px] rounded border border-slate-300 bg-white px-1 text-xs" data-testid="rpt-archive-header-row">
                {(inspect.preview_rows || []).map((r, i) => (
                  <option key={i} value={i}>Row {i + 1}: {r.filter(Boolean).slice(0, 6).join(" · ").slice(0, 70) || "(empty)"}</option>
                ))}
              </select>
            </label>
            <span className="text-slate-500">· <b data-testid="rpt-archive-rowcount">{inspect.row_count}</b> data rows · <b data-testid="rpt-archive-mapped">{mappedCount}</b> columns mapped</span>
            {inspect.existing && !edit && <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">already imported as “{inspect.existing.name}” — importing again replaces it</span>}
          </div>
          <MappingTable inspect={inspect} mapping={mapping} setMapping={setMapping} fields={inspect.fields} />
          <div className="flex items-center gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this worksheet (e.g. Orders 2019–2023)" className="h-9 text-sm w-72" data-testid="rpt-archive-name" />
            <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white ml-auto" disabled={!mappedCount || importMut.isPending} onClick={() => importMut.mutate()} data-testid="rpt-archive-import">
              {importMut.isPending ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileSpreadsheet size={14} className="mr-1" />} {edit || inspect.existing ? "Save mapping & re-import" : `Import ${inspect.row_count} rows`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function ArchiveDialog({ open, onClose }) {
  const [edit, setEdit] = useState(null);
  const arcQ = useQuery({
    queryKey: ["report-archives"], queryFn: async () => (await api.get("/report/archives")).data, enabled: open,
    refetchInterval: (q) => (q.state.data?.archives || []).some((a) => a.gallery_indexing) ? 4000 : false,
  });
  const archives = arcQ.data?.archives || [];
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setEdit(null); onClose(); } }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="rpt-archive-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet size={18} className="text-emerald-700" /> Old worksheets &amp; Drive photos</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <section data-testid="rpt-archive-section-list">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">1 · Your worksheets <span className="normal-case tracking-normal font-sans text-slate-400">— each with its Drive photo folder (folders named “… #PO”)</span></div>
            <ArchiveList archives={archives} onEdit={setEdit} />
          </section>
          <section className="border-t border-slate-200 pt-4" data-testid="rpt-archive-section-add">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{edit ? `Fix mapping — ${edit.name}` : "2 · Add a worksheet"} <span className="normal-case tracking-normal font-sans text-slate-400">— Google Sheet link or Excel / CSV file</span></div>
            <ArchiveImporter key={edit?.id || "new"} edit={edit} onDone={() => setEdit(null)} />
            {edit && <button type="button" className="mt-2 text-[11px] underline text-slate-500" onClick={() => setEdit(null)} data-testid="rpt-archive-edit-cancel">Cancel — add a new worksheet instead</button>}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
